import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useActiveProjectId, useServiceAuth } from '../../hooks';
import { ServiceAuth } from '../../hooks/useService/useService';
import FhirSearchService from '../../services/FhirSearchService';
import { DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM } from '../../services/MedicalResultsService';
import { REVIEWABLE_RESOURCE_TYPE_LIST } from '../../util/extractedResourceReview';
import {
  DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
  DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
  DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
} from '../SubjectViewer/Modules/Measurements/Graphs/Ranges/ExtractionFeedbackActions';

const messages = defineMessages({
  unknownSource: {
    id: 'phc-web-toolkit.memberExtractions.unknownSource',
    defaultMessage: 'Unknown source',
  },
});

const fhirSearchService = new FhirSearchService();

const PAGE_SIZE = 1000;
const UNKNOWN_SOURCE_REFERENCE = '';
const VISIT_NOTE_SYSTEM = 'http://lifeomic.com/fhir/fountain-visit-note';
const VISIT_NOTE_LABELS: Record<string, string> = {
  nonvisit: 'Non-Visit Note',
  visit: 'Visit Note',
  email: 'Email Note',
};

type CodeableConcept = {
  text?: string;
  coding?: Array<{ display?: string; code?: string }>;
};

type Quantity = {
  value?: number | string;
  unit?: string;
  code?: string;
};

type Dosage = {
  text?: string;
  doseQuantity?: Quantity;
};

export type ExtractedClinicalResource = {
  resourceType?: string;
  id?: string;
  code?: CodeableConcept;
  medicationCodeableConcept?: CodeableConcept;
  medicationReference?: { display?: string };
  vaccineCode?: CodeableConcept;
  valueQuantity?: Quantity;
  valueString?: string;
  valueBoolean?: boolean;
  valueDateTime?: string;
  valueTime?: string;
  valueCodeableConcept?: CodeableConcept;
  component?: Array<{
    code?: CodeableConcept;
    valueQuantity?: Quantity;
    valueString?: string;
    valueCodeableConcept?: CodeableConcept;
  }>;
  dosageInstruction?: Dosage[];
  dosage?: Dosage[];
  doseQuantity?: Quantity;
  reaction?: Array<{
    description?: string;
    manifestation?: CodeableConcept[];
  }>;
  extension?: Array<{
    url?: string;
    valueString?: string;
    valueDateTime?: string;
  }>;
};

export type MemberExtractionReviewDecision = 'approved' | 'rejected';

export type ExtractionProvenance = {
  recorded?: string;
  target?: Array<{ reference?: string }>;
  entity?: Array<{
    role?: string;
    whatReference?: { reference?: string };
  }>;
};

export type ExtractionSourceResource = {
  resourceType?: string;
  id?: string;
  type?: CodeableConcept;
  description?: string;
  content?: Array<{
    attachment?: { title?: string; contentType?: string; url?: string };
  }>;
  subject?: { reference?: string };
  category?: Array<{ text?: string }>;
  reasonCode?: Array<{ text?: string }>;
  payload?: Array<{ contentString?: string }>;
  meta?: { tag?: Array<{ system?: string; code?: string }> };
  questionnaire?: string | { display?: string };
};

export type MemberExtractionResourceRow = {
  id: string;
  resourceType: string;
  title: string;
  value?: string;
  reviewDecision?: MemberExtractionReviewDecision;
};

export type MemberExtractionPdf = {
  fileId: string;
  subjectId?: string;
};

export type MemberExtractionDocumentGroup = {
  sourceReference: string;
  title: string;
  resources: MemberExtractionResourceRow[];
  pdf?: MemberExtractionPdf;
  summaryReference?: string;
};

type SearchResponse<T> = {
  hits?: { hits?: Array<{ _source?: T }> };
};

const trimmed = (value?: string | null) => {
  const text = value?.trim();
  return text || undefined;
};

const conceptLabel = (concept?: CodeableConcept) =>
  trimmed(concept?.text) ||
  concept?.coding?.map(coding => trimmed(coding.display)).find(Boolean) ||
  concept?.coding?.map(coding => trimmed(coding.code)).find(Boolean);

const pascalToSnakeCase = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();

const pageLimit = (size: number) => [
  { type: 'number', value: 0 },
  { type: 'number', value: size },
];

export const getExtractedResourceTitle = (
  resource: ExtractedClinicalResource,
) =>
  conceptLabel(resource.code) ||
  conceptLabel(resource.medicationCodeableConcept) ||
  trimmed(resource.medicationReference?.display) ||
  conceptLabel(resource.vaccineCode) ||
  resource.resourceType ||
  'Untitled';

const formatQuantity = (quantity?: Quantity) => {
  if (
    quantity?.value === undefined ||
    quantity.value === null ||
    quantity.value === ''
  ) {
    return undefined;
  }
  const unit = trimmed(quantity.unit) || trimmed(quantity.code);
  return unit ? `${quantity.value} ${unit}` : String(quantity.value);
};

const formatObservationValue = (resource: ExtractedClinicalResource) => {
  const direct =
    formatQuantity(resource.valueQuantity) ||
    trimmed(resource.valueString) ||
    (resource.valueBoolean === undefined
      ? undefined
      : resource.valueBoolean
      ? 'Yes'
      : 'No') ||
    trimmed(resource.valueDateTime) ||
    trimmed(resource.valueTime) ||
    conceptLabel(resource.valueCodeableConcept);
  if (direct) {
    return direct;
  }
  const components = (resource.component ?? []).flatMap(component => {
    const value =
      formatQuantity(component.valueQuantity) ||
      trimmed(component.valueString) ||
      conceptLabel(component.valueCodeableConcept);
    if (!value) {
      return [];
    }
    const label = conceptLabel(component.code);
    return [label ? `${label} ${value}` : value];
  });
  return components.length > 0 ? components.join(' · ') : undefined;
};

const formatDosageValue = (doses?: Dosage[]) => {
  const parts = (doses ?? []).flatMap(dose => {
    const value = trimmed(dose.text) || formatQuantity(dose.doseQuantity);
    return value ? [value] : [];
  });
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

const formatAllergyValue = (resource: ExtractedClinicalResource) => {
  const parts = (resource.reaction ?? []).flatMap(reaction => {
    const manifestations = (reaction.manifestation ?? []).flatMap(concept => {
      const label = conceptLabel(concept);
      return label ? [label] : [];
    });
    const value =
      trimmed(reaction.description) ||
      (manifestations.length > 0 ? manifestations.join(', ') : undefined);
    return value ? [value] : [];
  });
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

export const getExtractedResourceValue = (
  resource: ExtractedClinicalResource,
) => {
  if (resource.resourceType === 'Observation') {
    return formatObservationValue(resource);
  }
  if (resource.resourceType === 'MedicationRequest') {
    return formatDosageValue(resource.dosageInstruction);
  }
  if (resource.resourceType === 'MedicationStatement') {
    return formatDosageValue(resource.dosage);
  }
  if (resource.resourceType === 'AllergyIntolerance') {
    return formatAllergyValue(resource);
  }
  if (resource.resourceType === 'Immunization') {
    return formatQuantity(resource.doseQuantity);
  }
  return undefined;
};

const extensionValue = (
  resource: ExtractedClinicalResource,
  url: string,
  key: 'valueString' | 'valueDateTime',
) => resource.extension?.find(extension => extension.url === url)?.[key];

export const getExtractedResourceReviewDecision = (
  resource: ExtractedClinicalResource,
): MemberExtractionReviewDecision | undefined => {
  const reviewStatus = extensionValue(
    resource,
    DOCUMENT_EXTRACTION_V2_REVIEW_STATUS_EXTENSION_URL,
    'valueString',
  );
  if (reviewStatus === 'approved' || reviewStatus === 'rejected') {
    return reviewStatus;
  }
  if (
    extensionValue(
      resource,
      DOCUMENT_EXTRACTION_V2_APPROVED_BY_EXTENSION_URL,
      'valueString',
    ) ||
    extensionValue(
      resource,
      DOCUMENT_EXTRACTION_V2_APPROVED_AT_EXTENSION_URL,
      'valueDateTime',
    )
  ) {
    return 'approved';
  }
  return undefined;
};

export const getExtractionSourceTitle = (
  source: ExtractionSourceResource | undefined,
  fallback: string,
) => {
  if (!source) {
    return fallback;
  }
  if (source.resourceType === 'DocumentReference') {
    return (
      trimmed(source.type?.text) ||
      source.type?.coding
        ?.map(coding => trimmed(coding.display))
        .find(Boolean) ||
      trimmed(source.description) ||
      trimmed(source.content?.[0]?.attachment?.title) ||
      fallback
    );
  }
  if (source.resourceType === 'Communication') {
    const code = source.meta?.tag?.find(
      tag => tag.system === VISIT_NOTE_SYSTEM,
    )?.code;
    const payload = trimmed(source.payload?.[0]?.contentString);
    return (
      (code && VISIT_NOTE_LABELS[code]) ||
      trimmed(code) ||
      trimmed(source.category?.[0]?.text) ||
      trimmed(source.reasonCode?.[0]?.text) ||
      (payload ? payload.slice(0, 80) : undefined) ||
      fallback
    );
  }
  if (source.resourceType === 'QuestionnaireResponse') {
    const questionnaire = source.questionnaire;
    const display =
      questionnaire && typeof questionnaire === 'object'
        ? trimmed(questionnaire.display)
        : undefined;
    return display || fallback;
  }
  return fallback;
};

const pdfForSource = (
  source: ExtractionSourceResource | undefined,
): MemberExtractionPdf | undefined => {
  if (source?.resourceType !== 'DocumentReference') {
    return undefined;
  }
  const attachment = source.content?.find(item => {
    const file = item.attachment;
    const contentType = file?.contentType?.split(';')[0]?.trim().toLowerCase();
    const name = `${file?.title ?? ''} ${file?.url ?? ''}`;
    return contentType === 'application/pdf' || /\.pdf(\?|#|$)/i.test(name);
  })?.attachment;
  const fileId = attachment?.url?.split('/').pop()?.split('?')[0];
  if (!fileId) {
    return undefined;
  }
  return {
    fileId,
    subjectId: source.subject?.reference?.split('/').pop(),
  };
};

export const buildMemberExtractionResourceQuery = () => ({
  type: 'select',
  columns: '*',
  from: REVIEWABLE_RESOURCE_TYPE_LIST.map(resourceType => ({
    table: pascalToSnakeCase(resourceType),
  })),
  limit: pageLimit(PAGE_SIZE),
  where: {
    type: 'elasticsearch',
    query: {
      term: {
        'identifier.system.keyword':
          DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM,
      },
    },
  },
});

// Patient search keeps only resources with subject or patient = the member.
// Provenance has neither, so it has to be read from the project index and
// limited to the extracted resources' target references.
// Activity fields are not part of the search: a term on
// activity.coding.*.keyword matches nothing when that path is mapped as
// nested, and then the whole query is empty even if the targets match.
export const buildMemberExtractionProvenanceQuery = (targets: string[]) => ({
  type: 'select',
  columns: '*',
  from: [{ table: 'provenance' }],
  limit: pageLimit(PAGE_SIZE),
  where: {
    type: 'elasticsearch',
    query: {
      bool: {
        should: [
          { terms: { 'target.reference.keyword': targets } },
          { terms: { 'target.reference': targets } },
        ],
        minimum_should_match: 1,
      },
    },
  },
});

export const buildMemberExtractionSourceQuery = (references: string[]) => {
  const ids = [
    ...new Set(
      references
        .map(reference => reference.split('/')[1])
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  return {
    type: 'select',
    columns: '*',
    from: [
      { table: 'document_reference' },
      { table: 'communication' },
      { table: 'questionnaire_response' },
    ],
    limit: pageLimit(Math.max(ids.length, 1)),
    where: {
      type: 'elasticsearch',
      query: {
        terms: { 'id.keyword': ids },
      },
    },
  };
};

const toRelativeReference = (reference?: string) => {
  if (!reference) {
    return undefined;
  }
  const match = reference.match(/([A-Za-z]+)\/([^/\s?#]+)$/);
  return match ? `${match[1]}/${match[2]}` : reference;
};

export const groupExtractionsBySource = (
  resources: ExtractedClinicalResource[],
  provenance: ExtractionProvenance[],
  sources: ExtractionSourceResource[],
  unknownSourceTitle: string,
): MemberExtractionDocumentGroup[] => {
  const sourceByTarget = new Map<string, string>();
  const summaryBySource = new Map<
    string,
    { reference: string; recorded: string }
  >();
  provenance.forEach(record => {
    const sourceReference = toRelativeReference(
      record.entity?.find(entity => entity.role === 'source')?.whatReference
        ?.reference,
    );
    if (!sourceReference) {
      return;
    }
    const summaryReference = toRelativeReference(
      record.entity?.find(entity => entity.role === 'derivation')?.whatReference
        ?.reference,
    );
    const recorded = record.recorded ?? '';
    const currentSummary = summaryBySource.get(sourceReference);
    if (
      summaryReference?.startsWith('DocumentReference/') &&
      (!currentSummary || recorded > currentSummary.recorded)
    ) {
      summaryBySource.set(sourceReference, {
        reference: summaryReference,
        recorded,
      });
    }
    record.target?.forEach(target => {
      const targetReference = toRelativeReference(target.reference);
      if (targetReference) {
        sourceByTarget.set(targetReference, sourceReference);
      }
    });
  });

  const sourceByReference = new Map<string, ExtractionSourceResource>();
  sources.forEach(source => {
    if (source.resourceType && source.id) {
      sourceByReference.set(`${source.resourceType}/${source.id}`, source);
    }
  });

  const groups = new Map<string, MemberExtractionResourceRow[]>();
  resources.forEach(resource => {
    if (!resource.resourceType || !resource.id) {
      return;
    }
    const sourceReference =
      sourceByTarget.get(`${resource.resourceType}/${resource.id}`) ??
      UNKNOWN_SOURCE_REFERENCE;
    const rows = groups.get(sourceReference) ?? [];
    rows.push({
      id: resource.id,
      resourceType: resource.resourceType,
      title: getExtractedResourceTitle(resource),
      value: getExtractedResourceValue(resource),
      reviewDecision: getExtractedResourceReviewDecision(resource),
    });
    groups.set(sourceReference, rows);
  });

  return [...groups.entries()]
    .map(([sourceReference, rows]) => ({
      sourceReference,
      title:
        sourceReference === UNKNOWN_SOURCE_REFERENCE
          ? unknownSourceTitle
          : getExtractionSourceTitle(
              sourceByReference.get(sourceReference),
              sourceReference,
            ),
      resources: [...rows].sort(
        (left, right) =>
          left.title.localeCompare(right.title) ||
          left.id.localeCompare(right.id),
      ),
      pdf: pdfForSource(sourceByReference.get(sourceReference)),
      summaryReference: summaryBySource.get(sourceReference)?.reference,
    }))
    .sort(
      (left, right) =>
        left.title.localeCompare(right.title) ||
        left.sourceReference.localeCompare(right.sourceReference),
    );
};

const hitsOf = <T>(
  response: { data?: SearchResponse<unknown> } | undefined,
): T[] =>
  (response?.data?.hits?.hits ?? [])
    .map(hit => hit._source)
    .filter((source): source is T => source != null);

const sourceReferencesFor = (
  resources: ExtractedClinicalResource[],
  provenance: ExtractionProvenance[],
) => {
  const targets = new Set(
    resources
      .filter(resource => resource.resourceType && resource.id)
      .map(resource => `${resource.resourceType}/${resource.id}`),
  );
  return [
    ...new Set(
      provenance.flatMap(record => {
        const sourceReference = toRelativeReference(
          record.entity?.find(entity => entity.role === 'source')?.whatReference
            ?.reference,
        );
        const matchesResource = record.target?.some(target => {
          const targetReference = toRelativeReference(target.reference);
          return Boolean(targetReference && targets.has(targetReference));
        });
        return sourceReference && matchesResource ? [sourceReference] : [];
      }),
    ),
  ];
};

export type MemberExtractionDocumentsResult = {
  groups: MemberExtractionDocumentGroup[];
  isLoading: boolean;
  error: unknown;
};

export const useMemberExtractionDocuments = (
  patientId: string | null | undefined,
): MemberExtractionDocumentsResult => {
  const { formatMessage } = useIntl();
  const auth = useServiceAuth();
  const projectId = useActiveProjectId();
  const [groups, setGroups] = React.useState<MemberExtractionDocumentGroup[]>(
    [],
  );
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<unknown>();

  React.useEffect(() => {
    const account = auth?.account;
    const authToken = auth?.authToken;
    if (!account || !authToken || !projectId || !patientId) {
      setGroups([]);
      setIsLoading(false);
      setError(undefined);
      return;
    }

    let cancelled = false;
    setGroups([]);
    setIsLoading(true);
    setError(undefined);

    const load = async () => {
      const searchPatient = fhirSearchService.searchPatient<
        SearchResponse<unknown>
      >(auth as ServiceAuth);
      const searchProject = fhirSearchService.search<SearchResponse<unknown>>(
        auth as ServiceAuth,
      );
      const resourceResponse = await searchPatient(
        projectId,
        patientId,
        buildMemberExtractionResourceQuery(),
        undefined,
        { queryId: 'member-extraction-documents' },
      );
      const resources = hitsOf<ExtractedClinicalResource>(resourceResponse);
      const targets = resources.flatMap(resource =>
        resource.resourceType && resource.id
          ? [`${resource.resourceType}/${resource.id}`]
          : [],
      );
      const provenance = targets.length
        ? hitsOf<ExtractionProvenance>(
            await searchProject(
              projectId,
              buildMemberExtractionProvenanceQuery(targets),
              undefined,
              { queryId: 'member-extraction-documents' },
            ),
          )
        : [];
      const references = sourceReferencesFor(resources, provenance);
      const sources = references.length
        ? hitsOf<ExtractionSourceResource>(
            await searchPatient(
              projectId,
              patientId,
              buildMemberExtractionSourceQuery(references),
              undefined,
              { queryId: 'member-extraction-documents' },
            ),
          )
        : [];
      return groupExtractionsBySource(
        resources,
        provenance,
        sources,
        formatMessage(messages.unknownSource),
      );
    };

    load()
      .then(nextGroups => {
        if (!cancelled) {
          setGroups(nextGroups);
          setIsLoading(false);
        }
      })
      .catch(nextError => {
        if (!cancelled) {
          setGroups([]);
          setError(nextError);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth, formatMessage, patientId, projectId]);

  return { groups, isLoading, error };
};
